import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './auth';
import { useQuery } from '@tanstack/react-query';

interface Organization {
  id: string;
  slug: string;
  business_name: string;
  logo_url?: string | null;
  domain?: string | null;
  custom_domain?: string | null;
  custom_domain_status?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  userRole: string;
  locale?: string | null;
  currency?: string | null;
  timezone?: string | null;
  vat_rate?: number | null;
  country_code?: string | null;
  property_services?: string[] | null;
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization) => void;
  loading: boolean;
  error: Error | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth();
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);

  const { data: organizations = [], isLoading, error } = useQuery<Organization[]>({
    queryKey: ['/api/organizations'],
    enabled: !!session,
    // Short stale window + window-focus refetch so changes made in the
    // Listings app (e.g. custom domain verification) surface here without
    // forcing a hard reload. See leadMagnetUrls / LeadMagnetLinkHub.
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });

  const loading = isLoading && !!session;

  useEffect(() => {
    if (!organizations.length) return;

    const storedOrgId = typeof window !== 'undefined' 
      ? localStorage.getItem('currentOrganizationId')
      : null;
    
    if (storedOrgId) {
      const stored = organizations.find(org => org.id === storedOrgId);
      if (stored) {
        setCurrentOrganizationState(stored);
        return;
      }
    }

    setCurrentOrganizationState(organizations[0]);
  }, [organizations]);

  const setCurrentOrganization = (org: Organization) => {
    setCurrentOrganizationState(org);
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentOrganizationId', org.id);
    }
  };

  return (
    <OrganizationContext.Provider value={{
      organizations,
      currentOrganization,
      setCurrentOrganization,
      loading,
      error: error as Error | null
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
